import { IBaseStore } from '../types'
import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators'
import Api from '../../api'
import {
    EBatchType,
    EStatementStatuses,
    IMailStatements,
    IStatement, IStatementBreakDownItem,
    IStatementListFilterDTO,
    IStatementMessage,
    IStatementStatisticsDTO,
    ILettersFiltersDTO,
    isString
} from '@zunta/configurations'
import { STATEMENT_MAILED_STATUSES } from '@/constants'

export interface IStatementState {
    selectedStatement: IStatement | null;
    statementList: IStatement[];
    statementListTotal: number;
    statistics: IStatementStatisticsDTO | null;
    trackingEvents: Array<any> | null;
    trackingLetterId: string | null;
    statementMessages: IStatementMessage[];
    batchStatements: IStatement[];
    statementBreakdownItems: IStatementBreakDownItem[];
    getNoPrebillItems: any[]
}

export interface IStatementStore extends IBaseStore {
    state: IStatementState
}

@Module({
    name: 'statement',
    namespaced: true
})
export class StatementModule extends VuexModule {
    statementList: IStatement[] = []
    statementListTotal: number = 0
    selectedStatement: IStatement | null = null
    statistics: IStatementStatisticsDTO | null = null
    trackingEvents: Array<any> | null = null
    statementMessages: IStatementMessage[] = []
    mailStatements: IMailStatements | null = null
    batchStatements: IStatement[] = []
    statementsType: EStatementStatuses | 'ALL' = 'ALL'
    setStopBilling: boolean | null = null

    shouldConfirmStopBilling: boolean = false
    statementBreakdownItems: IStatementBreakDownItem[] = []
    showSuccessModal: boolean = false
    shouldConfirmMail: boolean = false
    previousStatementId: number = 0
    getNoPrebillItems: any[] = [];
    trackingLetterId: string | null = null;

    @Mutation
    SET_STOP_BILLING_VALUE (value: boolean) {
        this.setStopBilling = value
    }

    @Mutation
    SET_SHOW_STOP_BILLING_MODAL (value: boolean) {
        this.shouldConfirmStopBilling = value
    }

    @Mutation
    SET_SHOW_CONFIRM_MAIL_MODAL (value: boolean) {
        this.shouldConfirmMail = value
    }

    @Mutation
    SET_SHOW_SUCCESS_MODAL (value: boolean) {
        this.showSuccessModal = value
    }

    @Mutation
    SET_PREVIOUS_STATEMENT_ID (value: number) {
        this.previousStatementId = value
    }

    @Mutation
    SET_STATEMENT_TYPE_FILTER (type: EStatementStatuses | 'ALL') {
        this.statementsType = type
    }

    @Mutation
    UPDATE_STATEMENT_PDF (input: { statementId: number | string, pdfUrl: string }) {
        if (this.selectedStatement) {
            this.selectedStatement.pdf = input.pdfUrl
        }

        const statement = this.statementList.find((statement: IStatement) => statement.id.toString() === input.statementId.toString())
        if (statement) {
            statement.pdf = input.pdfUrl
        }
    }

    @Mutation
    UPDATE_STATEMENT_MESSAGE_STATE (input: { statementMessageId: number, active: boolean }) {
        const message = this.statementMessages.find((message: IStatementMessage) => (message.id === input.statementMessageId))
        if (message) {
            message.active = input.active
        }
    }

    @Mutation
    UPDATE_STATEMENT_MESSAGES (messages: IStatementMessage[]) {
        this.statementMessages = messages
    }

    @Mutation
    SET_TRACKING_EVENTS (trackingEvents: Array<any> | null) {
        this.trackingEvents = trackingEvents
    }

    @Mutation
    SET_SELECTED_STATEMENT (statement: IStatement | null) {
        this.selectedStatement = statement
    }

    @Mutation
    UPDATE_SELECTED_STATEMENT (input: { statementId: number | string, data: Partial<IStatement> }) {
        const statement = this.statementList.find((statement: IStatement) => statement.id === parseInt(input.statementId.toString(), 10))
        Object.keys(input.data).forEach((key: string) => {
            (statement as any)[key] = (input.data as any)[key]
        })
    }

    @Mutation
    UPDATE_STATEMENT_STATISTICS (statistics: IStatementStatisticsDTO | null) {
        this.statistics = statistics
    }

    @Mutation
    UPDATE_STATEMENT_LIST (list: IStatement[]) {
        this.statementList = list
    }

    @Mutation
    UPDATE_STATEMENTS_TOTAL (total: number) {
        this.statementListTotal = total
    }

    @Mutation
    UPDATE_MAIL_STATEMENTS (statements: IMailStatements | null) {
        this.mailStatements = statements
    }

    @Mutation
    SET_RESIDENT_BATCH_STATEMENTS (statements: IStatement[]) {
        this.batchStatements = statements
    }

    @Action
    setStopBillingModalState (value: boolean) {
        this.SET_SHOW_STOP_BILLING_MODAL(value)
    }

    @Action
    setStopBillingValue (value: boolean) {
        this.SET_STOP_BILLING_VALUE(value)
    }

    @Mutation
    SET_STATEMENT_BREAKDOWN_ITEMS (items: IStatementBreakDownItem[]) {
        this.statementBreakdownItems = items
    }

    @Mutation
    SET_TRACKING_LETTER_ID (lobLetterId: string | null) {
      this.trackingLetterId = lobLetterId
    }

    @Action
    async fetchFacilitiesStatements (facilities: any[]): Promise<any> {
        return Api.statement.fetchFacilitiesStatements(facilities)
    }

    @Action
    async fetchFacilitiesNCSStatements (facilities: number[]): Promise<any> {
        return Api.statement.fetchFacilitiesNCSStatements(facilities)
    }

    @Action
    async fetchBatchStatements (input: { batchId: number | string, isUpdating: boolean }) {
        const { batchId, isUpdating } = input
        return Api.statement.fetchBatchStatements(batchId, { isUpdating })
    }

    @Action
    async fetchBatchNCSStatements (input: { batchId: number | string, isUpdating: boolean }): Promise<any> {
        const { batchId, isUpdating } = input
        return Api.statement.fetchBatchNCSStatements(batchId, { isUpdating })
    }

    @Action
    async getBatchStatementListByFilters (input: { filters: IStatementListFilterDTO, batchId: string | number }) {
        const { statements, total } = await Api.statement.getBatchStatementListByFilters(input.filters, input.batchId)
        if (statements) {
            this.UPDATE_STATEMENT_LIST(statements)
            this.UPDATE_STATEMENTS_TOTAL(total)
        }
    }

    @Action
    async changeStatementStatus (input: { statementId: number | string, status: EStatementStatuses, batch: number }) {
        const response = await Api.statement.changeStatementStatus(input.statementId, {
            status: input.status,
            batch: input.batch
        })
        if (response === 'OK') {
            this.UPDATE_SELECTED_STATEMENT({
                statementId: input.statementId,
                data: { status: input.status }
            })
        }
        return response
    }

    @Action
    async changeStatementsStatuses (payload: { statementIds: (number | string)[], status: EStatementStatuses, batchId: number }) {
        const { statementIds, status, batchId } = payload
        return Api.statement.updateStatementsByIds(statementIds, { status }, batchId)
    }

    @Action
    async fetchMailStatements (batchId: number | string) {
        const statements = await Api.statement.getMailStatements(batchId)
        this.UPDATE_MAIL_STATEMENTS(statements)
    }

    @Action
    clearMailStatements () {
        this.UPDATE_MAIL_STATEMENTS(null)
    }

    @Action
    async getResidentStatementListByFilters (input: { filters: IStatementListFilterDTO, residentId: string | number }) {
        const {
            statements,
            total
        } = await Api.statement.getResidentStatementListByFilters(input.filters, input.residentId)
        if (statements) {
            this.UPDATE_STATEMENT_LIST(statements)
            this.UPDATE_STATEMENTS_TOTAL(total)
        }
    }

    @Action
    async getIsUserAbleToSeeOpenBalance (residentId: string | number) {
        const filters = {
            skip: '0',
            limit: '5',
            statuses: STATEMENT_MAILED_STATUSES
        }
        const { statements } = await Api.statement.getResidentStatementListByFilters(filters, residentId)
        if (statements && statements.length) {
            statements.sort((a: any, b: any) => b.batch - a.batch)
            const filteredStatements = statements.filter((statement: IStatement) => statement.batch === statements[0].batch)
            return !filteredStatements.some((statement: IStatement) => statement.wasManuallyModified)
        }

        return true
    }

    @Action
    resetStatementList () {
        this.UPDATE_STATEMENT_LIST([])
        this.UPDATE_STATEMENTS_TOTAL(0)
    }

    @Action
    async getStatistics () {
        const statistics = await Api.statistics.getStatementsStatistics()
        this.UPDATE_STATEMENT_STATISTICS(statistics)
    }

    @Action
    async sendStatementsBatchMails (batchId: number | string): Promise<any> {
        return Api.statement.sendStatementsBatchMails(batchId)
    }

    @Action
    async fetchStatementById (statementId: number | string) {
        const statement = await Api.statement.getStatementById(statementId)
        this.SET_SELECTED_STATEMENT(statement)
    }

    @Action
    resetStatistics () {
        this.UPDATE_STATEMENT_STATISTICS(null)
    }

    @Action
    async fetchStatementTrackingInfo (statementId: number) {
        const trackingEvents = await Api.statement.getStatementTrackingInfo(statementId)
        if (!trackingEvents.statusCode) {
            this.SET_TRACKING_LETTER_ID(trackingEvents.lobLetterId)
            this.SET_TRACKING_EVENTS(trackingEvents.events)
        }
    }

    @Action
    async resetStatementTrackingEvents () {
        this.SET_TRACKING_EVENTS(null)
        this.SET_TRACKING_LETTER_ID(null)
    }

    @Action
    async fetchStatementMessages (data: { statementId: number | string, batchId: number | string }) {
        const statementMessages = await Api.statement.getStatementMessagesV2(data.statementId, data.batchId)
        this.UPDATE_STATEMENT_MESSAGES(statementMessages)
    }

    @Action
    async addStatementMessageLetterV2 (data: any) {
        const response = await Api.statement.addStatementMessageLetterV2(data)
        if (isString(response)) {
          this.UPDATE_STATEMENT_PDF({ statementId: data.currentStatementId, pdfUrl: response })
        }
        return response
    }

    @Action
    async updateResidentMessageLetterV2 (data: any) {
        const response = await Api.statement.updateStatementMessageLetterV2(data)
        if (isString(response)) {
          this.UPDATE_STATEMENT_PDF({ statementId: data.currentStatementId, pdfUrl: response })
        }
        return response
    }

    @Action
    async regenerateStatement (statementId: number | string) {
        let pdfUrl
        // @ts-ignore
        if ((this.state.batch.selectedBatch || { type: '' }).type === EBatchType.STATEMENTS) {
            pdfUrl = await Api.statement.regenerateStatement(statementId)
        } else {
            pdfUrl = await Api.statement.regenerateLetter(statementId)
        }
        this.UPDATE_STATEMENT_PDF({ statementId, pdfUrl })
    }

    @Action
    async changeStatementMessageActiveState (input: { statementMessageId: number, active: boolean }) {
        await Api.statement.changeStatementMessageActiveState(input.statementMessageId, input.active)
        this.UPDATE_STATEMENT_MESSAGE_STATE(input)
    }

    @Action
    async getReturnedBatchItemsListByFilters (input: { batchId: number | string, filters: IStatementListFilterDTO }) {
        const response = await Api.statement.fetchReturnedBatchItemsListByFilters(input.batchId, input.filters)
        if (response.items) {
            this.UPDATE_STATEMENT_LIST(response.items)
            this.UPDATE_STATEMENTS_TOTAL(response.total)
        }
    }

    @Action
    async sendLettersBatchMails (batchId: number | string): Promise<any> {
        await Api.statement.sendLettersBatchMails(batchId)
    }

    @Action
    async importNcsStatements (data: any): Promise<any> {
        return Api.statement.importNcsStatements(data.facilityId, data.privateFile, data.pendingFile)
    }

    @Action
    async updateStatements (data: any): Promise<any> {
        await Api.statement.updateStatements(data.facilityId, data.batchId, data.privateFile, data.pendingFile)
    }

    @Action
    async fetchResidentBatchStatements (data: any) {
        const response = await Api.statement.getResidentBatchStatements(data.residentId, data.batchId)
        this.SET_RESIDENT_BATCH_STATEMENTS(response)
        return response
    }

    @Action
    async fetchStatementLineItems (data: any): Promise<any> {
        return Api.statement.getStatementLineItems(data.statementId, data.includePreviousBreakdown)
    }

    @Action
    async updateStatementLineItems (data: any) {
        const response = await Api.statement.updateStatementLineItems(data.statementId, data.payload)
        if (isString(response)) {
          this.UPDATE_STATEMENT_PDF({ statementId: data.statementId, pdfUrl: response })
        }
        return response
    }

    @Action
    async revertStatement ({ statementId, forAll }: any) {
        const response = await Api.statement.revertStatement(statementId, forAll)
        if (isString(response)) {
          this.UPDATE_STATEMENT_PDF({ statementId, pdfUrl: response })
        }
        return response
    }

    @Action
    updateStatementFilterType (type: EStatementStatuses | 'ALL') {
        this.SET_STATEMENT_TYPE_FILTER(type)
    }

    @Action
    setConfirmMailModalState (value: boolean) {
        this.SET_SHOW_CONFIRM_MAIL_MODAL(value)
    }

    @Action
    setSuccessModalState (value: boolean) {
        this.SET_SHOW_SUCCESS_MODAL(value)
    }

    @Action
    setPreviousStatementId (value: number) {
        this.SET_PREVIOUS_STATEMENT_ID(value)
    }

    @Action
    resetResidentBatchStatements () {
        this.SET_RESIDENT_BATCH_STATEMENTS([])
    }

    @Action
    async fetchStatementsZip (input: { statementReferenceId: { id: number, type: 'resident' | 'batch' }, filtersDTO: IStatementListFilterDTO | ILettersFiltersDTO }): Promise<{
        message: string;
        hasCorruptFiles: boolean;
        status: number
    }> {
        const { statementReferenceId, filtersDTO } = input
        return Api.statement.fetchStatementsZip(statementReferenceId, filtersDTO)
    }

    @Action
    async getBatchStatementsNoCharges (batchId: number | string): Promise<any> {
        return Api.statement.fetchBatchStatementNoCharge(batchId)
    }

    @Action
    async getStatementBreakdownItems (statementId: number | string) {
        const response = await Api.statement.getStatementBreakdownItems(statementId)
        this.SET_STATEMENT_BREAKDOWN_ITEMS(response)
    }

    @Action
    async fetchNewChargesIsGenerated (batchId: number | string) {
        const response = await Api.statement.getNewChargesIsGenerated(batchId)
        this.SET_BATCH_PAYER_IF_NOT_PREBILL(response)
    }

    @Action
    async confirmNotPostedPopup (batchId: number) {
      return Api.statement.confirmNotPostedPopup(batchId)
    }

    @Mutation
    SET_BATCH_PAYER_IF_NOT_PREBILL (items: any[]) {
        this.getNoPrebillItems = items
    }
}
